import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import tableStyles from '../../../components/tables/table.module.scss';
import styles from './TherapiesExpandableTable.module.scss';
import StatusFlag from '../../../components/elements/StatusFlag';
import {
  getBooleanLabel,
  getBooleanTone,
} from '../../../shared/utils/statusTones';
import {
  getTherapyReservationStatusLabel,
  getTherapyStatusLabel,
} from '../utils/therapyPresentation';
import {
  getOccurrenceSecondaryStatusLabel,
  getOccurrenceStatusLabel,
} from '../utils/therapyOccurrencePresentation';
import { formatNameLastFirst } from '../../../shared/utils/formatName';

const activityTypeLabels = {
  created: 'DODATA TERAPIJA',
  updated: 'IZMENJENA TERAPIJA',
  renewed: 'OBNOVLJENA TERAPIJA',
  deleted: 'OBRISANA TERAPIJA',
  therapy_terminated: 'PREKINUTA TERAPIJA',
  status_changed: 'Promenjen status aktivnosti',
  occurrence_status_changed: 'Promenjen status aktivnosti',
  request_created: 'KREIRAN ZAHTEV',
  request_updated: 'IZMENJEN ZAHTEV',
  request_sent: 'POSLAT ZAHTEV',
  request_cancelled: 'OTKAZAN ZAHTEV',
  reservation_created: 'Kreirana rezervacija',
  reservation_updated: 'Izmenjena rezervacija',
  reservation_applied: 'Primenjena rezervacija',
  reservation_deleted: 'Obrisana rezervacija',
};

const fieldLabels = {
  name: 'Naziv terapije',
  medications: 'Lekovi',
  status: 'Status',
  'occurrence.status': 'Status aktivnosti',
  'occurrence.secondaryStatus': 'Sekundarni status aktivnosti',
  centralDate: 'Centralni datum',
  renew: 'Obnova',
  expiresAt: 'Vazi do',
  durationMonths: 'Trajanje',
  isAcute: 'Akutna terapija',
  userPickupMethod: 'Preuzimanje',
  pickupMethod: 'Preuzimanje',
  note: 'Napomena',
  userPhone: 'Broj telefona',
  userAddress: 'Adresa korisnika',
  requestedDeliveryDate: 'Zeljeni datum dostave',
  renewedFromTherapyId: 'Obnovljena iz prethodne terapije',
  'reservation.count': 'Broj rezervisanih aktivnosti',
  'reservation.medication': 'Lek',
  'reservation.note': 'Napomena',
  'reservation.reservedAt': 'Rezervisano',
  'reservation.status': 'Status rezervacije',
  'reservation.appliedAt': 'Primenjeno',
};

const hiddenHistoryFields = new Set([
  'description',
  'therapyId',
  'occurrenceId',
  '_id',
  'id',
]);

const shouldHideHistoryField = (field) => {
  if (!field) return false;

  const fieldName = String(field);
  const lastSegment = fieldName.split('.').pop();

  return (
    hiddenHistoryFields.has(fieldName) ||
    hiddenHistoryFields.has(lastSegment) ||
    (lastSegment?.endsWith('Id') && fieldName !== 'renewedFromTherapyId')
  );
};

const compactTherapyActivityTypes = new Set(['created', 'renewed']);

const shouldHideActivityHistoryField = (activityType, field) =>
  shouldHideHistoryField(field) ||
  (activityType === 'updated' && field === 'expiresAt') ||
  (compactTherapyActivityTypes.has(activityType) &&
    ['expiresAt', 'name', 'status'].includes(field));

const shouldHideActivityHistoryChange = (activityType, change) =>
  shouldHideActivityHistoryField(activityType, change?.field) ||
  (compactTherapyActivityTypes.has(activityType) &&
    change?.field === 'isAcute' &&
    change?.to !== true);

const renderBooleanLabel = (value) => {
  if (value === true) return 'Da';
  if (value === false) return 'Ne';
  return '-';
};

// Da/Ne kolone tabele; "-" kada podatak ne postoji (npr. red zahteva).
const renderBooleanFlag = (value) =>
  typeof value === 'boolean' ? (
    <StatusFlag tone={getBooleanTone(value)}>{getBooleanLabel(value)}</StatusFlag>
  ) : (
    '-'
  );

const formatDate = (value, format = 'DD.MM.YYYY.') => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : String(value);
};

const isDateTimeField = (field) =>
  ['reservation.reservedAt', 'reservation.appliedAt'].includes(field) ||
  String(field || '').endsWith('At');

const formatDurationMonths = (value) => {
  const months = Number(value);

  if (!months) return null;

  if (months === 1) return '1 mesec';
  return `${months} meseci`;
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const getOccurrenceStatusDisplayValue = (status, secondaryStatus) => {
  const statusLabel = getOccurrenceStatusLabel(status);
  const secondaryLabel = getOccurrenceSecondaryStatusLabel(secondaryStatus);

  if (!statusLabel || statusLabel === '-') {
    return secondaryLabel || '-';
  }

  return secondaryLabel ? `${statusLabel} (R)` : statusLabel;
};

const getOccurrenceStatusSummaryRow = (changes) => {
  const statusChange = changes.find(
    (change) => change?.field === 'occurrence.status'
  );
  const secondaryStatusChange = changes.find(
    (change) => change?.field === 'occurrence.secondaryStatus'
  );

  if (!statusChange && !secondaryStatusChange) {
    return null;
  }

  const statusFrom = hasValue(statusChange?.from)
    ? statusChange.from
    : statusChange?.to;
  const statusTo = hasValue(statusChange?.to)
    ? statusChange.to
    : statusChange?.from;
  const secondaryFrom = hasValue(secondaryStatusChange?.from)
    ? secondaryStatusChange.from
    : null;
  const secondaryTo = hasValue(secondaryStatusChange?.to)
    ? secondaryStatusChange.to
    : secondaryStatusChange?.from;

  const from = getOccurrenceStatusDisplayValue(statusFrom, secondaryFrom);
  const to = getOccurrenceStatusDisplayValue(statusTo, secondaryTo);
  const value = from && to && from !== to ? `${from} -> ${to}` : to || from;

  return {
    key: 'occurrence-status-summary',
    label: 'Status aktivnosti',
    value: value || '-',
  };
};

const formatStatusValue = (value, field) => {
  if (field === 'status') {
    return getTherapyStatusLabel(value);
  }

  if (field === 'occurrence.status') {
    return getOccurrenceStatusLabel(value);
  }

  if (field === 'occurrence.secondaryStatus') {
    return getOccurrenceSecondaryStatusLabel(value) || 'Bez sekundarnog statusa';
  }

  if (field === 'reservation.status') {
    return getTherapyReservationStatusLabel(value);
  }

  return null;
};

const formatValue = (value, field) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'boolean') return renderBooleanLabel(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (field === 'durationMonths') return formatDurationMonths(value);

  const statusValue = formatStatusValue(value, field);
  if (statusValue) return statusValue;

  if (
    field?.toLowerCase().includes('date') ||
    field === 'renew' ||
    field === 'expiresAt' ||
    isDateTimeField(field)
  ) {
    return formatDate(
      value,
      isDateTimeField(field) ? 'DD.MM.YYYY. HH:mm' : 'DD.MM.YYYY.'
    );
  }
  if (field === 'renewedFromTherapyId') {
    return 'Obnovljena iz prethodne terapije';
  }
  if (typeof value === 'object') {
    return value.name || value.label || value.title || JSON.stringify(value);
  }
  return String(value);
};

const getFieldLabel = (field) => {
  if (!field) return 'Promena';
  return fieldLabels[field] || field;
};

const getActivityTherapy = (activity) => activity?.therapy || activity || {};
const getActivityRequest = (activity) => activity?.request || null;

const getActivitySubject = (activity) => {
  const request = getActivityRequest(activity);

  if (request) {
    return {
      ...request,
      // Zahtev nosi podatke pacijenta pod user* nazivima.
      dateOfBirth: request.userDateOfBirth,
      healthBookletNumber: request.userHealthBookletNumber,
      userInternalNote: request.note,
    };
  }

  return getActivityTherapy(activity);
};

const getMedicationsLabel = (therapy) =>
  Array.isArray(therapy?.medications) && therapy.medications.length
    ? therapy.medications.join(', ')
    : '-';

const getMedicationsShortLabel = (therapy) =>
  Array.isArray(therapy?.medications) && therapy.medications.length
    ? therapy.medications.map((medication) => String(medication).split(' ')[0]).join(', ')
    : '-';

const getChangedByLabel = (activity) =>
  activity?.changedByName || activity?.changedByEmail || '';

const getRequestSummaryRows = (activity) => {
  const request = getActivityRequest(activity);

  if (!request) {
    return [];
  }

  return [
    {
      key: 'userName',
      label: 'Korisnik',
      value: formatNameLastFirst(request, '-'),
    },
    { key: 'userPhone', label: 'Broj telefona', value: request.userPhone || '-' },
    {
      key: 'userAddress',
      label: 'Adresa korisnika',
      value: request.userAddress || '-',
    },
    {
      key: 'pickupMethod',
      label: 'Preuzimanje',
      value: request.pickupMethod || '-',
    },
    {
      key: 'requestedDeliveryDate',
      label: 'Zeljeni datum dostave',
      value: formatDate(request.requestedDeliveryDate),
    },
    { key: 'status', label: 'Status zahteva', value: request.status || '-' },
    { key: 'note', label: 'Napomena', value: request.note || '-' },
  ].filter((row) => row.value !== '-');
};

const getChangeSummaryRows = (activity) => {
  const requestRows = getRequestSummaryRows(activity);

  if (requestRows.length) {
    return requestRows;
  }

  const therapy = getActivityTherapy(activity);
  const changes = Array.isArray(activity?.historyChanges)
    ? activity.historyChanges
    : [];

  if (changes.length) {
    const occurrenceStatusSummaryRow = getOccurrenceStatusSummaryRow(changes);
    const visibleRows = changes
      .filter(
        (change) =>
          !['occurrence.status', 'occurrence.secondaryStatus'].includes(
            change?.field
          )
      )
      .filter(
        (change) =>
          !shouldHideActivityHistoryChange(activity?.type, change)
      )
      .map((change, index) => {
        const from = formatValue(change?.from, change?.field);
        const to = formatValue(change?.to, change?.field);
        const separator = from && to ? ' -> ' : '';
        const value = [from, separator, to].filter(Boolean).join('');

        return {
          key: `${change?.field || 'change'}-${index}`,
          label:
            change?.field === 'renewedFromTherapyId'
              ? ''
              : getFieldLabel(change?.field),
          value: value || '-',
          from,
          to,
          hasChange: Boolean(from && to && from !== to),
        };
      });

    return occurrenceStatusSummaryRow
      ? [occurrenceStatusSummaryRow, ...visibleRows]
      : visibleRows;
  }

  if (activity?.type === 'created') {
    const rows = [
      { key: 'medications', label: 'Lekovi', value: getMedicationsLabel(therapy) },
      {
        key: 'centralDate',
        label: 'Centralni datum',
        value: formatDate(therapy?.centralDate),
      },
    ];

    if (therapy?.isAcute === true) {
      rows.push({
        key: 'isAcute',
        label: 'Akutna terapija',
        value: renderBooleanLabel(therapy.isAcute),
      });
    }

    return rows;
  }

  if (activity?.type === 'renewed') {
    const rows = [
      {
        key: 'duration',
        label: 'Trajanje',
        value: formatDurationMonths(therapy?.durationMonths) || '-',
      },
      {
        key: 'centralDate',
        label: 'Centralni datum',
        value: formatDate(therapy?.centralDate),
      },
      {
        key: 'renewed',
        label: '',
        value: 'Obnovljena iz prethodne terapije',
      },
    ];

    if (therapy?.isAcute === true) {
      rows.push({
        key: 'isAcute',
        label: 'Akutna terapija',
        value: renderBooleanLabel(therapy.isAcute),
      });
    }

    return rows;
  }

  return [];
};

const renderHistory = (activity) => {
  const changedAt = activity?.changedAt ? dayjs(activity.changedAt) : null;
  const time = changedAt?.isValid() ? changedAt.format('HH:mm') : '--:--';
  const changedBy = getChangedByLabel(activity);
  const rows = getChangeSummaryRows(activity);

  return (
    <div className={styles.historyContent}>
      <div className={styles.historyTitle}>
        {time} - {activityTypeLabels[activity?.type] || activity?.type || 'Aktivnost'}
      </div>
      {rows.length ? (
        <div className={styles.historyRows}>
          {rows.map((row) => (
            <div
              key={row.key}
              className={`${styles.historyRow} ${
                row.label ? '' : styles.historyRowFull
              }`}
            >
              {row.label ? (
                <span className={styles.historyRowLabel}>{row.label}:</span>
              ) : null}
              {row.hasChange ? (
                <span className={styles.historyChange}>
                  <span className={styles.historyChangeValue}>{row.from}</span>
                  <span className={styles.historyChangeArrow}>-&gt;</span>
                  <span className={styles.historyChangeValue}>{row.to}</span>
                </span>
              ) : (
                <span className={styles.historyRowValue}>{row.value}</span>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {changedBy ? (
        <div className={styles.historyMeta}>Izmenio: {changedBy}</div>
      ) : null}
      {activity?.note ? (
        <div className={styles.historyMeta}>Napomena: {activity.note}</div>
      ) : null}
    </div>
  );
};

const DailyActivitiesTable = ({
  data = [],
  emptyState = {
    icon: '/assets/icons/no-users.svg',
    title: 'Trenutno nema aktivnosti',
  },
}) => {
  const visibleData = data;

  return (
    <div className={styles.tableWrapper}>
      <table
        style={{ width: '100%', minWidth: 1280 }}
        className={styles.therapiesTable}
      >
        <colgroup>
          <col style={{ width: '12%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '5%' }} />
          <col style={{ width: '25%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Pacijent</th>
            <th>Datum rođenja</th>
            <th>LBO</th>
            <th>BZK</th>
            <th>Overena ZK</th>
            <th>Broj telefona</th>
            <th>Interna napomena</th>
            <th>Preuzimanje</th>
            <th>Lekovi</th>
            <th>App</th>
            <th className={styles.groupHeader}>Istorija izmena</th>
          </tr>
        </thead>
        <tbody>
          {visibleData.map((activity, index) => {
          const therapy = getActivitySubject(activity);
          // Ime pacijenta: "Prezime Ime" (koristi odvojena polja ako postoje,
          // inače obrće spojeni `userName` sa beka koji je "Ime Prezime").
          const patientName = formatNameLastFirst(therapy, '-');
          const rowKey = activity?._id || `${activity?.changedAt || 'activity'}-${index}`;

          return (
            <tr key={rowKey}>
              <td className={styles.topAlignedCell}>
                <span className={styles.wrapText} title={patientName}>
                  {patientName}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span className={styles.wrapText} title={therapy?.dateOfBirth || '-'}>
                  {therapy?.dateOfBirth ? dayjs(therapy.dateOfBirth).format('DD.MM.YYYY') : '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span className={styles.wrapText} title={therapy?.userLbo || '-'}>
                  {therapy?.userLbo || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span className={styles.wrapText} title={therapy?.healthBookletNumber || '-'}>
                  {therapy?.healthBookletNumber || '-'}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
                <span className={styles.nowrapText}>
                  {renderBooleanFlag(therapy?.isHealthBookletVerified)}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span className={styles.wrapText} title={therapy?.userPhone || '-'}>
                  {therapy?.userPhone || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span className={styles.wrapText} title={therapy?.userInternalNote || '-'}>
                  {therapy?.userInternalNote || '-'}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
                <span className={styles.nowrapText}>
                  {therapy?.userPickupMethod || 'APO'}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
                <span className={styles.wrapText} title={getMedicationsLabel(therapy)}>
                  {getMedicationsShortLabel(therapy)}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
                <span className={styles.nowrapText}>
                  {renderBooleanFlag(therapy?.haveApp)}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.historyCell}`}>
                {renderHistory(activity)}
              </td>
            </tr>
          );
          })}
          {visibleData.length === 0 && (
            <tr>
              <td colSpan={11} style={{ textAlign: 'center', padding: 8 }}>
                <div className={tableStyles.noData}>
                  <img src={emptyState.icon} alt="Nema podataka" />
                  <h3>{emptyState.title}</h3>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

DailyActivitiesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  emptyState: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
};

export default DailyActivitiesTable;
